import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Warning, CheckCircle, CaretDown, CaretRight, Phone, ArrowSquareOut,
  EnvelopeSimple, Eye, ShieldCheck,
} from '@phosphor-icons/react';
import API_URL from '../config';

const BOOKING_URL = 'https://meetings-na2.hubspot.com/joey-vitale/protection-plan-call';
const CLIENTS_EMAIL = 'clients@indielaw.com';

const LEVELS = {
  priority:  { label: 'Priority review',         bg: '#FEE2E2', fg: '#B91C1C', border: '#FCA5A5' },
  discuss:   { label: 'Maybe worth discussing',  bg: '#FEF3C7', fg: '#B45309', border: '#FCD34D' },
  awareness: { label: 'For your awareness',      bg: '#EEF2F7', fg: '#475569', border: '#CBD5E1' },
  none:      { label: 'No action needed',        bg: '#F1F5F3', fg: '#15803D', border: '#BBF7D0' },
};

const LEVEL_DEFAULTS = {
  priority: 'Something closely resembles your mark and may be worth acting on soon.',
  discuss: "Worth a quick conversation so we can decide together whether to act.",
  awareness: "Noted so you're in the loop — nothing to do right now.",
  none: 'Not a concern for your registration.',
};

const tsdrUrl = (serial) =>
  `https://tsdr.uspto.gov/#caseNumber=${encodeURIComponent(serial)}&caseSearchType=US_APPLICATION&caseType=DEFAULT&searchType=statusSearch`;

const truncate = (s, n = 60) => (s && s.length > n ? `${s.slice(0, n).trimEnd()}…` : s);

// Reformat an ISO date the portal receives into "Apr 14, 2026". Pass through anything else.
const fmtMonitorDate = (v) => {
  if (!v) return '';
  const s = String(v);
  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return s;
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

function askMailto(f, mark) {
  const subject = `Protection Plan question: ${f.mark_text} (Serial ${f.serial_number}) vs my ${mark}`;
  const body = [
    `Filing: ${f.mark_text}`,
    `Owner: ${f.owner}`,
    `Serial: ${f.serial_number}`,
    `Goods: ${f.plain_goods}`,
    `Level: ${f.action_label}`,
    `Why: ${f.why}`,
    '',
    'My question:',
    '',
  ].join('\n');
  return `mailto:${CLIENTS_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function levelExplanation(copy, level) {
  if (copy) {
    const nested = copy.levels || copy.level_explanations || copy.levelExplanations;
    if (nested && typeof nested[level] === 'string') return nested[level];
    for (const key of [level, `level_${level}`, `${level}_explanation`]) {
      if (typeof copy[key] === 'string') return copy[key];
    }
  }
  return LEVEL_DEFAULTS[level];
}

// Collapsible section used inside the panel — a clearly clickable full-width row.
function Collapsible({ title, defaultOpen = false, testid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border bg-white hover:bg-[#faf7f0] transition-colors cursor-pointer text-left"
        style={{ borderColor: '#e5dcc8' }}
        data-testid={`${testid}-toggle`}
      >
        <CaretRight
          weight="bold"
          className="w-4 h-4 flex-shrink-0 transition-transform duration-200"
          style={{ color: '#9a9488', transform: open ? 'rotate(90deg)' : 'none' }}
        />
        <span className="font-semibold text-sm flex-1" style={{ color: '#141b4d' }}>{title}</span>
        <span className="text-xs font-medium" style={{ color: '#9a9488' }}>{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && <div className="px-1 pt-3" data-testid={`${testid}-content`}>{children}</div>}
    </div>
  );
}

function FilingRow({ f, mark }) {
  const [open, setOpen] = useState(false);
  const lv = LEVELS[f.action_level] || LEVELS.none;
  const actionable = f.action_level === 'priority' || f.action_level === 'discuss';
  return (
    <div className="border-b border-[#F4F4F5] last:border-0" data-testid={`monitoring-filing-${f.serial_number}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-3 py-3 flex items-start gap-2.5 hover:bg-[#FAFAF8] transition-colors"
        data-testid={`monitoring-filing-toggle-${f.serial_number}`}
      >
        {open
          ? <CaretDown weight="bold" className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#9a9488' }} />
          : <CaretRight weight="bold" className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#9a9488' }} />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold" style={{ color: '#09090B' }}>{truncate(f.mark_text)}</span>
            {f.owner && <span className="text-sm" style={{ color: '#52525B' }}>· {f.owner}</span>}
            <span
              className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
              style={{ background: lv.bg, color: lv.fg, border: `1px solid ${lv.border}` }}
              data-testid={`monitoring-level-pill-${f.serial_number}`}
            >
              {f.action_label || lv.label}
            </span>
          </div>
          {f.why && <p className="text-sm mt-1" style={{ color: '#101820' }}>{f.why}</p>}
        </div>
      </button>
      {open && (
        <div className="px-3 pb-4 pl-9 space-y-2 text-sm" data-testid={`monitoring-filing-detail-${f.serial_number}`}>
          {f.plain_goods && <p style={{ color: '#101820' }}><span className="font-medium">Goods/services:</span> {f.plain_goods}</p>}

          {actionable ? (
            <>
              {f.filed && <p style={{ color: '#52525B' }}><span className="font-medium" style={{ color: '#101820' }}>Filed:</span> {fmtMonitorDate(f.filed)}</p>}
              {f.status_note && <p style={{ color: '#101820' }}>{f.status_note}</p>}
              {f.deadline_text && <p className="font-semibold" style={{ color: '#B45309' }}>{f.deadline_text}</p>}
              {f.recommendation && f.recommendation_approved && (
                <p className="font-medium" style={{ color: '#141b4d' }}>Our recommendation: {f.recommendation}</p>
              )}
            </>
          ) : (
            // awareness / none: status_line in neutral grey only; never amber/red, never recommendation text.
            f.status_line
              ? <p style={{ color: '#52525B' }} data-testid={`monitoring-statusline-${f.serial_number}`}>{f.status_line}</p>
              : (f.filed && <p style={{ color: '#52525B' }}>Filed {fmtMonitorDate(f.filed)}</p>)
          )}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <a
              href={tsdrUrl(f.serial_number)} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold hover:underline"
              style={{ color: '#1d57a5' }}
              data-testid={`monitoring-tsdr-${f.serial_number}`}
            >
              View on USPTO <ArrowSquareOut weight="bold" className="w-3 h-3" />
            </a>
            {actionable && (
              <>
                <a
                  href={askMailto(f, mark)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-[#141b4d] text-white hover:bg-[#1d2461] transition-colors"
                  data-testid={`monitoring-ask-${f.serial_number}`}
                >
                  <EnvelopeSimple weight="bold" className="w-3.5 h-3.5" /> Ask us about this
                </a>
                <a
                  href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 border border-[#141b4d]/30 text-[#141b4d] hover:bg-[#141b4d]/5 transition-colors"
                  data-testid={`monitoring-vip-${f.serial_number}`}
                >
                  <Phone weight="bold" className="w-3.5 h-3.5" /> Discuss on my VIP call
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function GoodsLine({ cls, goods, testid }) {
  const [expanded, setExpanded] = useState(false);
  const long = goods && goods.length > 200;
  const text = long && !expanded ? `${goods.slice(0, 200).trimEnd()}…` : goods;
  return (
    <p className="text-sm" style={{ color: '#101820' }} data-testid={testid}>
      {cls && <span className="font-semibold">Class {cls}:</span>} {text}
      {long && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="ml-1 font-semibold hover:underline"
          style={{ color: '#1d57a5' }}
          data-testid={`${testid}-showfull`}
        >
          Show full description
        </button>
      )}
    </p>
  );
}

function MarkCard({ md }) {
  const regs = md.registrations || [];
  const classes = md.our_classes || [];
  const goods = md.our_goods || [];
  const hasFound = md.found_count !== null && md.found_count !== undefined;
  const foundMsg = hasFound && md.found_count < 3
    ? `We found only ${md.found_count} filing${md.found_count === 1 ? '' : 's'} resembling this mark this quarter.`
    : hasFound && md.found_count > md.shown_count
      ? `We reviewed ${md.found_count} filings resembling this mark and are showing the closest.`
      : '';
  const regLine = [
    regs.length > 0 ? `Reg. No. ${regs.join(', ')}` : '',
    classes.length > 0 ? `Classes ${classes.join(', ')}` : '',
  ].filter(Boolean).join(' · ');
  return (
    <div className="border border-[#E4E4E7] bg-white" data-testid={`monitoring-mark-${md.mark}`}>
      <div className="px-4 py-3 bg-[#FAFAF8] border-b border-[#E4E4E7]">
        <p className="font-bold" style={{ color: '#141b4d', fontFamily: 'Cabinet Grotesk, sans-serif' }}>{md.mark}</p>
        {regLine && (
          <p className="text-xs mt-0.5" style={{ color: '#52525B' }} data-testid={`monitoring-regline-${md.mark}`}>{regLine}</p>
        )}
        {goods.length > 0 && (
          <div className="mt-2 space-y-1" data-testid={`monitoring-coverage-${md.mark}`}>
            {goods.map((g, i) => (
              <GoodsLine key={i} cls={g.class} goods={g.goods} testid={`monitoring-coverage-line-${md.mark}-${i}`} />
            ))}
          </div>
        )}
      </div>
      {foundMsg && (
        <p className="px-4 py-2 text-xs" style={{ color: '#52525B', background: '#FFFDF5' }}
          data-testid={`monitoring-foundmsg-${md.mark}`}>{foundMsg}</p>
      )}
      <div>
        {md.filings.map((f, i) => <FilingRow key={f.serial_number || i} f={f} mark={md.mark} />)}
      </div>
    </div>
  );
}

// ---- Legacy fallback (packages without marks_detail) ----
function LegacyBlock({ q }) {
  return (
    <div className="space-y-4" data-testid="monitoring-legacy">
      {q.needs_look.length > 0 && (
        <div className="space-y-3">
          {q.needs_look.map((f, i) => (
            <div key={i} className="p-4 border-l-4 bg-white" style={{ borderColor: '#DC2626' }}>
              <p className="text-base font-bold" style={{ color: '#09090B' }}>{f.filing}</p>
              {f.owner && <p className="text-sm" style={{ color: '#52525B' }}>Filed by {f.owner}</p>}
              {f.goods && <p className="text-sm mt-1" style={{ color: '#101820' }}>{f.goods}</p>}
              {f.recommendation && <p className="text-sm mt-2 font-medium" style={{ color: '#141b4d' }}>Our recommendation: {f.recommendation}</p>}
            </div>
          ))}
        </div>
      )}
      {q.watching.length > 0 && (
        <div>
          <h4 className="text-sm font-bold uppercase tracking-wide mb-2 flex items-center gap-2" style={{ color: '#B45309' }}>
            <Eye weight="bold" className="w-4 h-4" /> Still watching
          </h4>
          <div className="space-y-2">
            {q.watching.map((f, i) => (
              <div key={i} className="p-3 border border-[#E4E4E7] bg-white">
                <p className="text-sm font-semibold" style={{ color: '#09090B' }}>{f.filing}
                  {f.owner && <span className="font-normal" style={{ color: '#52525B' }}> · {f.owner}</span>}</p>
                {f.goods && <p className="text-sm" style={{ color: '#101820' }}>{f.goods}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      {q.marks.length > 0 && (
        <div>
          <h4 className="text-sm font-bold uppercase tracking-wide mb-2 flex items-center gap-2" style={{ color: '#141b4d' }}>
            <ShieldCheck weight="bold" className="w-4 h-4" /> Marks we monitored
          </h4>
          <div className="overflow-hidden border border-[#E4E4E7] bg-white">
            <table className="w-full text-sm"><tbody>
              {q.marks.map((m, i) => (
                <tr key={i} className="border-b border-[#F4F4F5] last:border-0">
                  <td className="px-3 py-2 font-medium" style={{ color: '#09090B' }}>{m.mark}</td>
                  <td className="px-3 py-2 text-right" style={{ color: '#52525B' }}>
                    {m.pending ? 'Monitoring starts once it registers' : (m.registration_number ? `Reg. ${m.registration_number}` : 'Monitored')}
                  </td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </div>
      )}
      {q.no_action && q.no_action.length > 0 && (
        <details data-testid="monitoring-noaction">
          <summary className="text-sm font-medium cursor-pointer" style={{ color: '#52525B' }}>
            Filings we noticed — no action needed ({q.no_action.length})
          </summary>
          <div className="mt-2 overflow-hidden border border-[#E4E4E7] bg-white">
            <table className="w-full text-sm"><tbody>
              {q.no_action.map((f, i) => (
                <tr key={i} className="border-b border-[#F4F4F5] last:border-0">
                  <td className="px-3 py-2" style={{ color: '#09090B' }}>{f.filing}</td>
                  <td className="px-3 py-2 text-right" style={{ color: '#52525B' }}>{f.owner || ''}</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </details>
      )}
    </div>
  );
}

function LegendContent({ copy }) {
  return (
    <div className="space-y-2.5">
      {['priority', 'discuss', 'awareness', 'none'].map((lvl) => {
        const lv = LEVELS[lvl];
        return (
          <div key={lvl} className="flex items-start gap-3">
            <span
              className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 mt-0.5"
              style={{ background: lv.bg, color: lv.fg, border: `1px solid ${lv.border}` }}
            >
              {lv.label}
            </span>
            <p className="text-sm" style={{ color: '#52525B' }}>{levelExplanation(copy, lvl)}</p>
          </div>
        );
      })}
    </div>
  );
}

function AboutContent({ periodWord }) {
  const Block = ({ title, children }) => (
    <div>
      <p className="font-semibold" style={{ color: '#09090B' }}>{title}</p>
      <p className="mt-0.5" style={{ color: '#3f3f46' }}>{children}</p>
    </div>
  );
  return (
    <div className="space-y-3 text-sm">
      <Block title="What this monitoring covers, and why">
        Each {periodWord} we review new applications filed at the USPTO that resemble your registered marks. Catching
        look-alikes early keeps your brand strong, and stops other marks from chipping away at (diluting) what makes
        yours distinctive.
      </Block>
      <Block title="What it doesn't cover">
        We review USPTO filings only, not unregistered business names, social media handles or domain names. USPTO
        filings are where a competing claim to rights becomes official, so that's where monitoring has the most impact.
      </Block>
      <Block title="Why we don't rush to act">
        Enforcing your rights against near-identical marks keeps your brand protected. But acting too fast can backfire,
        for example if the other business turns out to have used its name before you did. So we always check the facts first.
      </Block>
      <Block title="If something here concerns you">
        The options include opposing the application, starting a cancellation proceeding, sending a cease-and-desist
        letter, or negotiating a coexistence agreement so the other business stays out of your core offerings. These are
        often combined. We'll help you choose what fits.
      </Block>
      <Block title="What's included, and what isn't">
        Your Protection Plan includes this monitoring and a quarterly strategy call with an attorney to talk through
        anything you see here. Taking action, such as an opposition, a cancellation, a cease-and-desist letter or a
        coexistence agreement, isn't part of the plan. If you'd like to go that route, we'll talk it through and give you
        a clear quote before any work begins.
      </Block>
      <a
        href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm font-semibold mt-1"
        style={{ color: '#1d57a5' }}
        data-testid="monitoring-about-vip"
      >
        <Phone weight="bold" className="w-4 h-4" /> Book my VIP call
      </a>
    </div>
  );
}

export default function QuarterlyMonitoring({ cadence }) {
  const [latest, setLatest] = useState(undefined);
  const [firstView, setFirstView] = useState(false);
  const every = cadence?.every || 'every quarter';
  const periodWord = cadence?.adjective === 'monthly' ? 'month' : 'quarter';

  useEffect(() => {
    axios.get(`${API_URL}/api/client/monitoring`)
      .then((r) => { setLatest(r.data.latest || null); setFirstView(!!r.data.first_view); })
      .catch(() => setLatest(null));
  }, []);

  if (latest === undefined || latest === null) return null;

  const copy = latest.copy_blocks || {};
  const attention = latest.attention_count > 0;

  return (
    <div
      data-testid="quarterly-monitoring"
      className="rounded-xl border p-5 sm:p-6 space-y-5"
      style={{ background: '#f7f2e8', borderColor: '#e5dcc8' }}
    >
      {/* 1. Heading + status banner */}
      <h2 className="text-xl font-semibold" style={{ color: '#09090B', fontFamily: 'Cabinet Grotesk, sans-serif' }}
        data-testid="monitoring-heading">
        Your {latest.quarter_label} monitoring update
      </h2>

      {attention ? (
        <div className="border-l-4 p-4" style={{ borderColor: '#F59E0B', background: '#FFFBEB' }}
          data-testid="monitoring-summary-amber">
          <div className="flex items-start gap-3">
            <Warning weight="fill" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#B45309' }} />
            <div>
              <p className="font-bold" style={{ color: '#09090B' }}>
                {latest.attention_count} filing{latest.attention_count === 1 ? '' : 's'} worth a closer look
              </p>
              {latest.attention_marks?.length > 0 && (
                <p className="text-sm mt-0.5" style={{ color: '#101820' }}>
                  Near your mark{latest.attention_marks.length > 1 ? 's' : ''}: <strong>{latest.attention_marks.join(', ')}</strong>
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="border-l-4 p-4" style={{ borderColor: '#22C55E', background: '#F0FDF4' }}
          data-testid="monitoring-summary-green">
          <div className="flex items-start gap-3">
            <CheckCircle weight="fill" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#16A34A' }} />
            <p className="text-sm" style={{ color: '#101820' }}>
              We checked your registered marks for <strong>{latest.quarter_label}</strong>. Nothing needs your attention.
            </p>
          </div>
        </div>
      )}

      {/* 2. Per-mark cards (or legacy fallback) */}
      {latest.has_marks_detail ? (
        <div className="space-y-4" data-testid="monitoring-mark-cards">
          {latest.marks_detail.map((md, i) => <MarkCard key={md.mark || i} md={md} />)}
        </div>
      ) : (
        <LegacyBlock q={latest} />
      )}

      {/* before_we_act (flagged filings present) — just above "About this monitoring" */}
      {copy.before_we_act && (
        <div className="border p-4" style={{ background: '#ffffff', borderColor: '#CBD5E1' }} data-testid="monitoring-before-we-act">
          <p className="font-semibold mb-1" style={{ color: '#09090B' }}>Before we act</p>
          <p className="text-sm" style={{ color: '#52525B' }}>{copy.before_we_act}</p>
        </div>
      )}

      {/* 3. About this monitoring — open on first view, collapsed after */}
      <Collapsible title="About this monitoring" defaultOpen={firstView} testid="monitoring-about">
        <AboutContent periodWord={periodWord} />
      </Collapsible>

      {/* 4. What these levels mean — collapsed by default */}
      {latest.has_marks_detail && (
        <Collapsible title="What these levels mean" defaultOpen={false} testid="monitoring-legend">
          <LegendContent copy={copy} />
        </Collapsible>
      )}

      {/* Closing line (panel's last line) */}
      <p className="text-sm border-t pt-4" style={{ color: '#52525B', borderColor: '#e5dcc8' }} data-testid="monitoring-closing">
        You'll get an update here {every}, and we'll email you right away if something needs a decision before then.
      </p>
    </div>
  );
}
